import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { toast } from "sonner";
import "@/scanner.css";
import {
  LogOut,
  ScanLine,
  Package,
  AlertCircle,
  CheckCircle2,
  Loader2,
  Camera,
  ArrowRight,
  History,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Scanner = ({ token, user, operario, tipoTrabajo, operarioNombre, estadoDestino, onLogout }) => {
  const navigate = useNavigate();
  const [scanning, setScanning] = useState(true);
  const [manualCode, setManualCode] = useState("");
  const [articleInfo, setArticleInfo] = useState(null);
  const [pedidoInfo, setPedidoInfo] = useState(null);
  const [tipoCodigo, setTipoCodigo] = useState(null); // "parte" o "pedido"
  const [unitsToProcess, setUnitsToProcess] = useState("");
  const [processing, setProcessing] = useState(false);
  const [cameraReady, setCameraReady] = useState(false);
  const [lastScannedCode, setLastScannedCode] = useState("");
  const [scanningBlocked, setScanningBlocked] = useState(false);
  const [articleHistory, setArticleHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [undoingHistory, setUndoingHistory] = useState(null);
  
  // UseRef para mantener UNA ÚNICA instancia del scanner a través de todos los renders
  const scannerRef = useRef(null);
  const isInitializingRef = useRef(false);
  const isMountedRef = useRef(false);
  
  // UseRef para control de escaneo en tiempo real (evita closures)
  const scanningBlockedRef = useRef(false);
  const lastScannedCodeRef = useRef("");

  // Función para reproducir sonido de escaneo
  const playBeepSound = () => {
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      
      // Crear oscilador para el tono
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      // Configurar sonido típico de lector de código de barras
      oscillator.frequency.value = 2800; // Frecuencia alta (Hz)
      oscillator.type = 'square'; // Onda cuadrada para sonido más "digital"
      
      // Envolvente de volumen (fade in/out rápido)
      gainNode.gain.setValueAtTime(0, audioContext.currentTime);
      gainNode.gain.linearRampToValueAtTime(0.3, audioContext.currentTime + 0.01);
      gainNode.gain.linearRampToValueAtTime(0.3, audioContext.currentTime + 0.08);
      gainNode.gain.linearRampToValueAtTime(0, audioContext.currentTime + 0.1);
      
      // Reproducir
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.1);
    } catch (error) {
      console.log('No se pudo reproducir el sonido:', error);
    }
  };

  useEffect(() => {
    // Marcar que el componente está montado
    isMountedRef.current = true;
    
    console.log('🚀 useEffect: Componente montado - Iniciando cámara...');
    
    // Auto-start camera on mount - Simple y directo
    const initCamera = async () => {
      if (!scannerRef.current && !isInitializingRef.current) {
        await startScanning();
      }
    };
    
    initCamera();
    
    return () => {
      console.log('🧹 Cleanup: Componente desmontándose');
      isMountedRef.current = false;
      
      if (scannerRef.current) {
        console.log('🛑 Deteniendo scanner...');
        scannerRef.current.stop().catch(err => console.log('Error al detener:', err));
        scannerRef.current.clear();
        scannerRef.current = null;
      }
      isInitializingRef.current = false;
    };
  }, []);

  const startScanning = async () => {
    // PROTECCIÓN ROBUSTA contra múltiples instancias usando useRef
    if (scannerRef.current || isInitializingRef.current) {
      console.log('🛑 Cámara ya activa o iniciándose, bloqueando...');
      return;
    }
    
    console.log('🎥 Iniciando cámara...');
    isInitializingRef.current = true;
    setScanning(true);
    
    try {
      // Limpiar completamente cualquier instancia previa del DOM
      const qrReaderElement = document.getElementById('qr-reader');
      if (qrReaderElement) {
        qrReaderElement.innerHTML = ''; // Limpiar contenido
        console.log('🧹 DOM limpiado');
      }
      
      // Import Html5Qrcode class for direct camera control
      const { Html5Qrcode } = await import('html5-qrcode');
      
      const html5QrCode = new Html5Qrcode("qr-reader");
      
      // Configuración optimizada para móvil
      // QR Box pequeño y horizontal para códigos de barras
      const config = {
        fps: 10,
        qrbox: { width: 180, height: 60 },  // Estrecho y horizontal para códigos de barras
        aspectRatio: 1.777,  // 16:9 para mejor captura de códigos horizontales
      };
      
      try {
        // MÉTODO 1: Usar facingMode "environment" (cámara trasera)
        // Este es el método estándar y más confiable
        await html5QrCode.start(
          { facingMode: "environment" },  // Solicitar cámara trasera
          config,
          (decodedText) => {
            // Solo verificar, NO bloquear aquí (el bloqueo se hace en handleScan)
            if (!scanningBlockedRef.current && decodedText !== lastScannedCodeRef.current) {
              handleScan(decodedText);
            }
          },
          (errorMessage) => {
            // Ignore frequent scanning errors
          }
        );
        
        // Guardar instancia en ref (NO en state)
        scannerRef.current = html5QrCode;
        setCameraReady(true);
        isInitializingRef.current = false; // Marcar que terminó la inicialización
        console.log('✅ Cámara activada correctamente');
        toast.success("Cámara trasera activada. Escanea tu código de barras.");
        
      } catch (facingModeError) {
        console.log("⚠️ Método facingMode falló, intentando método de ID de dispositivo...", facingModeError);
        
        try {
          // MÉTODO 2: Fallback - Buscar cámara trasera por ID
          const devices = await Html5Qrcode.getCameras();
          console.log(`📷 Dispositivos de cámara encontrados: ${devices.length}`);
          
          if (devices && devices.length > 0) {
            // Por defecto usar la última cámara (suele ser la trasera)
            let cameraId = devices[devices.length - 1].id;
            
            // Intentar encontrar cámara trasera por label
            for (let device of devices) {
              const label = device.label.toLowerCase();
              console.log(`📷 Dispositivo: ${device.label}`);
              if (label.includes('back') || 
                  label.includes('rear') ||
                  label.includes('trasera') ||
                  label.includes('posterior') ||
                  label.includes('environment') ||
                  label.includes('facing back')) {
                cameraId = device.id;
                console.log("✅ Cámara trasera encontrada:", device.label);
                break;
              }
            }
            
            // Start scanning with selected camera
            await html5QrCode.start(
              cameraId,
              config,
              (decodedText) => {
                // Solo verificar, NO bloquear aquí (el bloqueo se hace en handleScan)
                if (!scanningBlockedRef.current && decodedText !== lastScannedCodeRef.current) {
                  handleScan(decodedText);
                }
              },
              (errorMessage) => {
                // Ignore frequent scanning errors
              }
            );
            
            // Guardar instancia en ref (NO en state)
            scannerRef.current = html5QrCode;
            setCameraReady(true);
            isInitializingRef.current = false; // Marcar que terminó la inicialización
            console.log('✅ Cámara activada correctamente (fallback)');
            toast.success("Cámara activada. Escanea tu código de barras.");
          } else {
            throw new Error("No se encontraron cámaras disponibles");
          }
        } catch (fallbackError) {
          console.error("❌ Método fallback también falló:", fallbackError);
          // Lanzar el error para que lo capture el catch externo
          throw fallbackError;
        }
      }
    } catch (err) {
      console.error("❌ Error starting camera:", err);
      isInitializingRef.current = false; // Resetear flag en caso de error
      scannerRef.current = null; // Limpiar instancia en caso de error
      
      if (err.name === 'NotFoundError' || err.message?.includes('not found')) {
        toast.warning("No se detectó cámara. Puedes usar entrada manual de código.", {
          duration: 5000,
        });
      } else if (err.name === 'NotAllowedError' || err.message?.includes('Permission')) {
        toast.error("Permisos de cámara denegados. Por favor, actívalos en la configuración de tu navegador.", {
          duration: 7000,
        });
      } else if (err.name === 'NotReadableError' || err.message?.includes('in use')) {
        toast.error("La cámara está siendo usada por otra aplicación. Cierra otras apps y recarga.", {
          duration: 7000,
        });
      } else {
        toast.error(`Error al acceder a la cámara: ${err.message || 'Desconocido'}. Usa entrada manual.`, {
          duration: 7000,
        });
      }
      
      setScanning(false);
      setCameraReady(false);
    }
  };

  const stopScanning = async () => {
    if (scannerRef.current) {
      try {
        await scannerRef.current.stop();
        scannerRef.current.clear();
        scannerRef.current = null;
      } catch (err) {
        console.error("Error stopping scanner:", err);
      }
    }
    setScanning(false);
  };

  const loadArticleHistory = async (codigoBarras) => {
    if (!codigoBarras) {
      setArticleHistory([]);
      return;
    }
    
    setLoadingHistory(true);
    let timeoutId = null;
    try {
      // Timeout de 10 segundos para evitar que se quede colgado
      const controller = new AbortController();
      timeoutId = setTimeout(() => controller.abort(), 10000);
      
      // No enviamos fecha para obtener historial de todas las fechas
      // IMPORTANTE: Aplicar filtro por tipo de trabajo (molduras/puertas)
      const response = await axios.get(`${API}/history`, {
        params: { 
          codigo_barras: codigoBarras,
          todas_fechas: true,  // Parámetro especial para obtener todo el historial
          tipo_articulo: tipoTrabajo  // Aplicar filtro por tipo de trabajo seleccionado
        },
        headers: { Authorization: `Bearer ${token}` },
        signal: controller.signal,
        timeout: 10000,  // Timeout adicional de axios
      });
      
      if (timeoutId) clearTimeout(timeoutId);
      // Ordenar historial por fecha/hora descendente (más reciente primero)
      const historialOrdenado = (response.data.historial || []).sort((a, b) => {
        // Comparar por fecha primero
        if (a.FechaProceso && b.FechaProceso) {
          const fechaA = new Date(a.FechaProceso);
          const fechaB = new Date(b.FechaProceso);
          if (fechaA.getTime() !== fechaB.getTime()) {
            return fechaB.getTime() - fechaA.getTime(); // DESC
          }
        }
        // Si las fechas son iguales, comparar por hora
        const horaA = a.HoraProceso || 0;
        const horaB = b.HoraProceso || 0;
        return horaB - horaA; // DESC
      });
      setArticleHistory(historialOrdenado);
    } catch (error) {
      if (timeoutId) clearTimeout(timeoutId);
      console.error("Error loading article history:", error);
      
      if (error.name === 'AbortError' || error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        toast.error("Timeout al cargar historial. Intenta de nuevo.");
      } else if (error.response) {
        toast.error(`Error al cargar historial: ${error.response.data?.detail || error.message}`);
      } else {
        toast.error("Error al cargar historial. Verifica la conexión.");
      }
      
      setArticleHistory([]);
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleUndoFromScanner = async (item) => {
    if (!window.confirm(
      `¿Deshacer proceso ${item.TipoProceso}?\n\n` +
      `Unidades: ${item.UnidPendProceso}`
    )) {
      return;
    }

    setUndoingHistory(item.IdCBParte);
    try {
      const response = await axios.post(
        `${API}/undo-process`,
        {
          IdCBParte: item.IdCBParte,
          CodigoBarrasParte: item.CodigoBarrasParte,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.data.success) {
        toast.success("Operación deshecha correctamente");
        
        // Recargar información del artículo desde el servidor
        if (articleInfo) {
          try {
            const scanResponse = await axios.post(
              `${API}/scan`,
              { barcode: articleInfo.CodigoBarrasParte },  // Usar CodigoBarrasParte, no CodigoBarrasParcial
              {
                headers: { Authorization: `Bearer ${token}` },
              }
            );
            
            // El endpoint /api/scan devuelve { TipoCodigo, ArticleInfo, PedidoInfo }
            const { TipoCodigo, ArticleInfo } = scanResponse.data;
            
            if (TipoCodigo === "parte" && ArticleInfo) {
              // Actualizar toda la información del artículo
              setArticleInfo(ArticleInfo);
              setUnitsToProcess(ArticleInfo.UnidPendProceso.toString());
              
              // Recargar historial
              await loadArticleHistory(ArticleInfo.CodigoBarrasParte);
              
              // Resetear bloqueo de escaneo
              setLastScannedCode("");
              setScanningBlocked(false);
              
              toast.info("Artículo actualizado - Ahora puedes procesarlo de nuevo");
            } else {
              toast.warning("No se pudo recargar el artículo");
            }
          } catch (error) {
            console.error("Error reloading article:", error);
            toast.error("Error al recargar artículo");
          }
        }
      }
    } catch (error) {
      console.error("Error undoing:", error);
      const errorMsg = error.response?.data?.detail || "Error al deshacer operación";
      toast.error(errorMsg);
    } finally {
      setUndoingHistory(null);
    }
  };

  const handleScan = async (barcode) => {
    // Evitar escaneos duplicados del mismo código
    if (scanningBlockedRef.current || lastScannedCodeRef.current === barcode) {
      return;
    }

    // Bloquear escaneos por 3 segundos (en ref Y estado)
    scanningBlockedRef.current = true;
    lastScannedCodeRef.current = barcode;
    setScanningBlocked(true);
    setLastScannedCode(barcode);
    
    // Pausar el escáner temporalmente si existe
    if (scannerRef.current) {
      try {
        await scannerRef.current.pause();
      } catch (e) {
        console.log("No se pudo pausar el escáner:", e);
      }
    }
    
    try {
      const response = await axios.post(
        `${API}/scan`,
        { barcode },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const { TipoCodigo, ArticleInfo, PedidoInfo } = response.data;
      
      // Reproducir sonido de escaneo exitoso
      playBeepSound();
      
      setTipoCodigo(TipoCodigo);
      
      if (TipoCodigo === "parte") {
        // Comportamiento actual: escaneo de artículo individual
        setArticleInfo(ArticleInfo);
        setPedidoInfo(null);
        setUnitsToProcess(ArticleInfo.UnidPendProceso.toString());
        toast.success("Artículo escaneado correctamente");
        
        // Cargar historial del artículo
        await loadArticleHistory(ArticleInfo.CodigoBarrasParte);
      } else if (TipoCodigo === "pedido") {
        // Filtrar artículos según el tipo de trabajo seleccionado
        const articulosFiltrados = {
          ...PedidoInfo,
          ArticulosPendientes: PedidoInfo.ArticulosPendientes.filter(art => 
            tipoTrabajo === 'molduras' ? art.TipoArticulo === 'GALCE' : art.TipoArticulo === 'PUERTA'
          ),
          ArticulosProcesados: PedidoInfo.ArticulosProcesados.filter(art => 
            tipoTrabajo === 'molduras' ? art.TipoArticulo === 'GALCE' : art.TipoArticulo === 'PUERTA'
          )
        };
        
        // Recalcular totales después del filtrado
        articulosFiltrados.TotalPendientes = articulosFiltrados.ArticulosPendientes.length;
        articulosFiltrados.TotalProcesados = articulosFiltrados.ArticulosProcesados.length;
        articulosFiltrados.TotalArticulos = articulosFiltrados.TotalPendientes + articulosFiltrados.TotalProcesados;
        
        setPedidoInfo(articulosFiltrados);
        setArticleInfo(null);
        
        const tipoTexto = tipoTrabajo === 'molduras' ? 'Molduras' : 'Puertas';
        toast.success(`Pedido escaneado (${tipoTexto}): ${articulosFiltrados.TotalArticulos} artículos encontrados`);
      }
    } catch (error) {
      console.error("Scan error:", error);
      toast.error(
        error.response?.data?.detail || "Error al escanear el código"
      );
      // Si hay error, reanudar escáner y permitir escanear de nuevo
      if (scannerRef.current) {
        try {
          await scannerRef.current.resume();
        } catch (e) {
          console.log("No se pudo reanudar el escáner:", e);
        }
      }
      // Limpiar refs y estado
      scanningBlockedRef.current = false;
      lastScannedCodeRef.current = "";
      setScanningBlocked(false);
      setLastScannedCode("");
    } finally {
      // Desbloquear después de 2 segundos y reanudar escáner
      setTimeout(async () => {
        // Limpiar TANTO refs como estado
        scanningBlockedRef.current = false;
        lastScannedCodeRef.current = "";
        setScanningBlocked(false);
        setLastScannedCode("");
        
        // Reanudar el escáner si no hay artículo cargado
        if (scannerRef.current && !articleInfo && !pedidoInfo) {
          try {
            await scannerRef.current.resume();
          } catch (e) {
            console.log("No se pudo reanudar el escáner:", e);
          }
        }
      }, 2000); // 2 segundos de espera
    }
  };

  const handleManualScan = (e) => {
    e.preventDefault();
    if (manualCode.trim()) {
      setLastScannedCode(""); // Reset para permitir escaneo manual
      setScanningBlocked(false);
      handleScan(manualCode.trim());
      setManualCode("");
    }
  };

  const handleProcess = async () => {
    if (!articleInfo || !unitsToProcess) {
      toast.error("Por favor ingrese las unidades a procesar");
      return;
    }

    const units = parseFloat(unitsToProcess);
    if (isNaN(units) || units <= 0) {
      toast.error("Ingrese una cantidad válida de unidades");
      return;
    }

    if (units > articleInfo.UnidPendProceso) {
      const confirmed = window.confirm(
        `Va a procesar ${units} unidades pero solo quedan ${articleInfo.UnidPendProceso} pendientes. ¿Desea continuar?`
      );
      if (!confirmed) return;
    }

    setProcessing(true);

    try {
      const response = await axios.post(
        `${API}/process`,
        {
          IdCBParte: articleInfo.IdCBParte,
          UnidadesAProcesar: units,
          Operario: operario ? parseInt(operario) : 0,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      toast.success(response.data.message);
      setArticleInfo(null);
      setUnitsToProcess("");
      
      // Restart camera after 1 second
      setTimeout(() => {
        if (!scannerRef.current) {
          startScanning();
        }
      }, 1000);
    } catch (error) {
      console.error("Process error:", error);
      toast.error(
        error.response?.data?.detail || "Error al procesar el artículo"
      );
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="min-h-screen p-4 md:p-6" data-testid="scanner-container">
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <Card className="border-0 shadow-lg bg-white/95 backdrop-blur" data-testid="header-card">
          <CardHeader className="pb-4">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center" data-testid="header-icon">
                  <Package className="w-6 h-6 text-white" />
                </div>
                <div>
                  <CardTitle className="text-xl" style={{ fontFamily: 'Space Grotesk, sans-serif' }} data-testid="header-title">
                    Control de Producción
                  </CardTitle>
                  <p className="text-sm text-muted-foreground" data-testid="user-info">
                    Operario:{" "}
                    <span className="font-semibold">
                      {operarioNombre || (user ? `Operario ${user}` : "Sin identificar")}
                    </span>
                    {user && (
                      <span className="text-xs text-gray-500 ml-2">
                        ({user})
                      </span>
                    )}
                  </p>
                  {estadoDestino && (
                    <p className="text-xs text-gray-500 mt-1">
                      Estado destino configurado:{" "}
                      <span className="font-semibold">{estadoDestino}</span>
                    </p>
                  )}
                  <Badge 
                    className={`${
                      tipoTrabajo === 'molduras' 
                        ? 'bg-blue-500' 
                        : 'bg-green-500'
                    } text-white`}
                  >
                    {tipoTrabajo === 'molduras' ? 'Molduras' : 'Puertas'}
                  </Badge>
                </div>
              </div>
              <div className="flex gap-2 flex-shrink-0">
                <Button
                  variant="outline"
                  size="default"
                  onClick={() => {
                    // En móvil, usar window.location para forzar recarga completa
                    if (/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)) {
                      window.location.href = '/history';
                    } else {
                      navigate('/history');
                    }
                  }}
                  className="gap-2 h-11 min-w-[110px] px-4"
                  data-testid="history-button"
                >
                  <History className="w-5 h-5" />
                  <span>Historial</span>
                </Button>
                <Button
                  variant="outline"
                  size="default"
                  onClick={onLogout}
                  className="gap-2 h-11 min-w-[90px] px-4"
                  data-testid="logout-button"
                >
                  <LogOut className="w-5 h-5" />
                  <span>Salir</span>
                </Button>
              </div>
            </div>
          </CardHeader>
        </Card>

        {/* Scanner Section - Always Visible */}
        <Card className="border-0 shadow-lg" data-testid="scanner-section">
          <CardHeader>
            <CardTitle className="flex items-center gap-2" data-testid="scanner-title">
              <Camera className="w-5 h-5 text-indigo-600" />
              Visor de Cámara
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Camera Scanner - Always Active */}
            <div className="space-y-3" data-testid="scanner-active">
              <div className="bg-black rounded-lg overflow-hidden shadow-xl relative">
                <div id="qr-reader" style={{width: '100%', minHeight: '200px', maxHeight: '250px'}}></div>
                {!cameraReady && scanning && (
                  <div className="absolute inset-0 flex items-center justify-center bg-black/80">
                    <div className="text-center text-white p-4">
                      <Loader2 className="w-8 h-8 animate-spin mx-auto mb-2" />
                      <p className="text-sm">Iniciando cámara...</p>
                      <p className="text-xs text-gray-400 mt-2">Asegúrate de otorgar permisos</p>
                    </div>
                  </div>
                )}
                {!cameraReady && !scanning && (
                  <div className="absolute inset-0 flex items-center justify-center bg-black/80">
                    <div className="text-center text-white p-4 max-w-sm">
                      <AlertCircle className="w-12 h-12 mx-auto mb-3 text-red-400" />
                      <p className="text-sm mb-2 font-semibold">No se pudo iniciar la cámara</p>
                      <p className="text-xs text-gray-300 mb-4">
                        Verifica los permisos de cámara en tu navegador y recarga la página
                      </p>
                      <Button 
                        onClick={() => {
                          console.log('🔄 Reintentando iniciar cámara...');
                          setScanning(true);
                          startScanning();
                        }}
                        variant="outline"
                        size="sm"
                        className="bg-white text-black hover:bg-gray-200 w-full"
                      >
                        🔄 Reintentar Cámara
                      </Button>
                      <p className="text-xs text-gray-400 mt-3">O usa entrada manual abajo ⬇️</p>
                    </div>
                  </div>
                )}
              </div>
              {scanning && cameraReady && (
                <div className="text-center">
                  <Badge variant="outline" className="bg-green-50 text-green-700 border-green-300">
                    <div className="w-2 h-2 bg-green-500 rounded-full mr-2 animate-pulse"></div>
                    Cámara Activa - Escanea tu código
                  </Badge>
                </div>
              )}
            </div>
            
            {/* Instrucciones de Escaneo */}
            {cameraReady && !articleInfo && !pedidoInfo && (
              <Alert className="border-blue-200 bg-blue-50">
                <AlertCircle className="h-4 w-4 text-blue-600" />
                <AlertDescription className="text-xs text-blue-900">
                  <strong>Consejo:</strong> Centra UN SOLO código de barras en el marco rectangular. 
                  Si hay códigos juntos, apunta solo al que quieres leer.
                </AlertDescription>
              </Alert>
            )}

            {/* Manual Input - Collapsed */}
            {!articleInfo && !pedidoInfo && (
              <form onSubmit={handleManualScan} className="space-y-2" data-testid="manual-scan-form">
                <Label htmlFor="manual-code" className="text-xs text-muted-foreground" data-testid="manual-code-label">O ingresa código manual</Label>
                <div className="flex gap-2">
                  <Input
                    id="manual-code"
                    type="text"
                    placeholder="Código de barras (parte o pedido)"
                    value={manualCode}
                    onChange={(e) => setManualCode(e.target.value)}
                    className="flex-1 h-10 text-sm"
                    data-testid="manual-code-input"
                  />
                  <Button type="submit" size="sm" className="h-10" data-testid="manual-scan-button">
                    Buscar
                  </Button>
                </div>
              </form>
            )}
          </CardContent>
        </Card>

        {/* Pedido Info */}
        {pedidoInfo && (
          <Card className="border-0 shadow-2xl overflow-hidden" data-testid="pedido-info-card">
            {/* Header */}
            <div className="p-6 bg-gradient-to-br from-purple-500 to-indigo-600">
              <div className="flex items-center justify-between text-white">
                <div className="flex items-center gap-3">
                  <div className="w-14 h-14 bg-white/20 backdrop-blur rounded-2xl flex items-center justify-center">
                    <Package className="w-8 h-8" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                      Pedido
                    </h3>
                    <p className="text-sm text-white/90">{pedidoInfo.CodigoBarrasPedido}</p>
                  </div>
                </div>
              </div>
            </div>

            <CardContent className="p-6 space-y-6">
              {/* Resumen */}
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-blue-50 p-4 rounded-lg text-center">
                  <div className="text-2xl font-bold text-blue-600">{pedidoInfo.TotalArticulos}</div>
                  <div className="text-xs text-blue-600 mt-1">Total</div>
                </div>
                <div className="bg-amber-50 p-4 rounded-lg text-center">
                  <div className="text-2xl font-bold text-amber-600">{pedidoInfo.TotalPendientes}</div>
                  <div className="text-xs text-amber-600 mt-1">Pendientes</div>
                </div>
                <div className="bg-green-50 p-4 rounded-lg text-center">
                  <div className="text-2xl font-bold text-green-600">{pedidoInfo.TotalProcesados}</div>
                  <div className="text-xs text-green-600 mt-1">Procesados</div>
                </div>
              </div>

              {/* Artículos Pendientes */}
              {pedidoInfo.ArticulosPendientes.length > 0 && (
                <div>
                  <h4 className="font-semibold text-lg mb-3 flex items-center gap-2">
                    <Badge className="bg-amber-500">Pendientes de Procesar</Badge>
                  </h4>
                  <div className="space-y-3">
                    {pedidoInfo.ArticulosPendientes.map((articulo) => (
                      <Card key={articulo.IdCBParte} className="border border-amber-200 bg-amber-50/50">
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <Badge variant="outline" className={`${
                                  articulo.TipoArticulo === "GALCE" 
                                    ? "bg-blue-50 text-blue-700 border-blue-300"
                                    : "bg-green-50 text-green-700 border-green-300"
                                }`}>
                                  {articulo.TipoArticulo}
                                </Badge>
                                <span className="text-xs text-gray-500">{articulo.CodigoBarrasParcial}</span>
                              </div>
                              <p className="font-medium text-sm truncate">{articulo.DescripcionArticulo}</p>
                              <p className="text-xs text-gray-600 mt-1">
                                Código: {articulo.CodigoArticulo} | Serie: {articulo.SerieParteFabrica}
                              </p>
                            </div>
                            <div className="text-right flex-shrink-0">
                              <div className="text-lg font-bold text-amber-600">{articulo.UnidPendProceso}</div>
                              <div className="text-xs text-gray-500">unidades</div>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              )}

              {/* Artículos Procesados */}
              {pedidoInfo.ArticulosProcesados.length > 0 && (
                <div>
                  <h4 className="font-semibold text-lg mb-3 flex items-center gap-2">
                    <Badge className="bg-green-500">Ya Procesados</Badge>
                  </h4>
                  <div className="space-y-3">
                    {pedidoInfo.ArticulosProcesados.map((articulo) => (
                      <Card key={articulo.IdCBParte} className="border border-green-200 bg-green-50/50">
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <Badge variant="outline" className={`${
                                  articulo.TipoArticulo === "GALCE" 
                                    ? "bg-blue-50 text-blue-700 border-blue-300"
                                    : "bg-green-50 text-green-700 border-green-300"
                                }`}>
                                  {articulo.TipoArticulo}
                                </Badge>
                                <span className="text-xs text-gray-500">{articulo.CodigoBarrasParcial}</span>
                                <CheckCircle2 className="w-4 h-4 text-green-600" />
                              </div>
                              <p className="font-medium text-sm truncate">{articulo.DescripcionArticulo}</p>
                              <p className="text-xs text-gray-600 mt-1">
                                Código: {articulo.CodigoArticulo} | Serie: {articulo.SerieParteFabrica}
                              </p>
                            </div>
                            <div className="text-right flex-shrink-0">
                              <Badge className="bg-green-600">Estado {articulo.EstadoProcesoFab}</Badge>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              )}

              {/* Botón para volver a escanear */}
              <Button 
                onClick={() => {
                  setPedidoInfo(null);
                  setTipoCodigo(null);
                  setLastScannedCode("");
                  startScanning();
                }}
                className="w-full"
                variant="outline"
              >
                <Camera className="w-4 h-4 mr-2" />
                Escanear Otro Código
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Article Info - COMPACTO */}
        {articleInfo && (
          <Card className="border-0 shadow-lg" data-testid="article-info-card">
            <CardContent className="p-4 space-y-3">
              {/* Info Compacta */}
              <div className={`flex items-center justify-between p-3 rounded-lg border ${
                articleInfo.TipoArticulo === "GALCE"
                  ? "bg-blue-50 border-blue-200"
                  : "bg-green-50 border-green-200"
              }`}>
                <div>
                  <Badge className={`${
                    articleInfo.TipoArticulo === "GALCE" ? "bg-blue-600" : "bg-green-600"
                  } text-white text-xs`}>
                    {articleInfo.TipoArticulo}
                  </Badge>
                  <div className="text-xs text-gray-600 mt-1">
                    Estado 1 → {articleInfo.EstadoDestino}
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-2xl font-bold text-orange-600">
                    {articleInfo.UnidPendProceso}
                  </div>
                  <div className="text-xs text-gray-500">pendientes</div>
                </div>
              </div>

              {/* Descripción Compacta */}
              <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                <p className="text-sm font-medium text-gray-900 leading-tight" data-testid="description-value">
                  {articleInfo.DescripcionArticulo}
                </p>
                {articleInfo.Descripcion2Articulo && (
                  <p className="text-xs text-gray-600 mt-1" data-testid="description2-value">
                    {articleInfo.Descripcion2Articulo}
                  </p>
                )}
              </div>

              {/* Mensaje si ya está procesado pero SIN unidades pendientes */}
              {articleInfo.YaProcesado && articleInfo.UnidPendProceso === 0 ? (
                <div>
                  <Alert className="bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-300">
                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0 w-10 h-10 bg-green-200 rounded-full flex items-center justify-center">
                        <CheckCircle2 className="w-6 h-6 text-green-700" />
                      </div>
                      <div className="flex-1">
                        <AlertDescription className="text-base text-green-900">
                          <div className="font-bold mb-2">✅ Artículo Completamente Procesado</div>
                          <p className="text-sm">
                            Este artículo ya está en estado{" "}
                            <span className="font-bold">
                              {articleInfo.EstadoProcesoFab === 11 ? "MOLDURA (11)" : "PUERTA PG (21)"}
                            </span>
                          </p>
                          <p className="text-sm mt-2 text-green-700">
                            No hay unidades pendientes. Puedes ver el historial abajo y deshacer operaciones si es necesario.
                          </p>
                        </AlertDescription>
                      </div>
                    </div>
                  </Alert>
                  
                  {/* Botón solo para cancelar/volver */}
                  <div className="flex gap-3 mt-4">
                    <Button
                      onClick={() => {
                        setArticleInfo(null);
                        setUnitsToProcess("");
                        setLastScannedCode("");
                        setScanningBlocked(false);
                        setArticleHistory([]);
                        toast.info("Escanea otro código de barras");
                      }}
                      variant="outline"
                      className="w-full h-14 px-6 border-2"
                      data-testid="cancel-button"
                    >
                      Escanear Otro Artículo
                    </Button>
                  </div>
                </div>
              ) : articleInfo.EstadoProcesoFab !== 1 ? (
                <div>
                  <Alert className="bg-gradient-to-r from-yellow-50 to-amber-50 border-2 border-yellow-300">
                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0 w-10 h-10 bg-yellow-200 rounded-full flex items-center justify-center">
                        <AlertCircle className="w-6 h-6 text-yellow-700" />
                      </div>
                      <div className="flex-1">
                        <AlertDescription className="text-base text-yellow-900">
                          <div className="font-bold mb-2">⚠️ Artículo Parcialmente Procesado</div>
                          <p className="text-sm">
                            Hay {articleInfo.UnidPendProceso} unidades pendientes de este artículo.
                          </p>
                          <p className="text-sm mt-2 text-yellow-700">
                            Puedes deshacer operaciones del historial para liberar más unidades y procesarlas.
                          </p>
                        </AlertDescription>
                      </div>
                    </div>
                  </Alert>
                  
                  {/* Botón solo para cancelar/volver */}
                  <div className="flex gap-3 mt-4">
                    <Button
                      onClick={() => {
                        setArticleInfo(null);
                        setUnitsToProcess("");
                        setLastScannedCode("");
                        setScanningBlocked(false);
                        setArticleHistory([]);
                        toast.info("Escanea otro código de barras");
                      }}
                      variant="outline"
                      className="w-full h-14 px-6 border-2"
                      data-testid="cancel-button"
                    >
                      Escanear Otro Artículo
                    </Button>
                  </div>
                </div>
              ) : (
                <>
                  {/* Código de barras y Unidades a procesar - Dividido 50/50 */}
                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-gray-700">
                      Código leído y Unidades a procesar:
                    </Label>
                    <div className="grid grid-cols-2 gap-2 sm:gap-3">
                      {/* Código de barras - Mitad izquierda */}
                      <div className="space-y-1">
                        <Label htmlFor="barcode-display" className="text-xs text-gray-500">
                          Código de Barras
                        </Label>
                        <div
                          id="barcode-display"
                          className="h-12 text-sm sm:text-base md:text-lg font-bold text-center border-2 border-gray-300 rounded-md bg-gray-50 flex items-center justify-center px-2 text-gray-700 overflow-hidden"
                          data-testid="barcode-display"
                          title={articleInfo.CodigoBarrasParte}
                          style={{ 
                            fontFamily: 'monospace',
                            fontSize: 'clamp(0.875rem, 3vw, 1.125rem)',
                            letterSpacing: '-0.2px',
                            lineHeight: '1.2'
                          }}
                        >
                          <span className="truncate w-full px-1" style={{ maxWidth: '100%' }}>{articleInfo.CodigoBarrasParte}</span>
                        </div>
                      </div>
                      {/* Unidades a procesar - Mitad derecha */}
                      <div className="space-y-1">
                        <Label htmlFor="units" className="text-xs text-gray-500" data-testid="units-to-process-label">
                          Unidades
                        </Label>
                        <Input
                          id="units"
                          type="number"
                          step="0.01"
                          min="0"
                          max={articleInfo.UnidPendProceso}
                          value={unitsToProcess}
                          onChange={(e) => setUnitsToProcess(e.target.value)}
                          placeholder="Cantidad"
                          className="h-12 text-base sm:text-lg md:text-xl font-bold text-center border-2"
                          data-testid="units-input"
                          autoFocus
                        />
                      </div>
                    </div>
                  </div>

                  {/* Botones de Acción - COMPACTOS */}
                  <div className="flex gap-2">
                    <Button
                      onClick={handleProcess}
                      disabled={processing || !unitsToProcess || parseFloat(unitsToProcess) <= 0}
                      className={`flex-1 h-12 px-4 font-semibold ${
                        articleInfo.TipoArticulo === "GALCE"
                          ? "bg-blue-600 hover:bg-blue-700"
                          : "bg-green-600 hover:bg-green-700"
                      }`}
                      data-testid="process-button"
                    >
                      {processing ? (
                        <>
                          <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                          Procesando...
                        </>
                      ) : (
                        <>
                          <CheckCircle2 className="mr-2 h-5 w-5" />
                          Procesar
                        </>
                      )}
                    </Button>
                    <Button
                      onClick={() => {
                        setArticleInfo(null);
                        setUnitsToProcess("");
                        setLastScannedCode("");
                        setScanningBlocked(false);
                        setArticleHistory([]);
                      }}
                      variant="outline"
                      className="h-12 px-4 border-2"
                      data-testid="cancel-button"
                    >
                      Cancelar
                    </Button>
                  </div>
                </>
              )}

              {/* Historial del artículo */}
              {(articleHistory.length > 0 || loadingHistory) && (
                <div className="mt-6">
                  <Separator className="my-4" />
                  <div className="flex items-center gap-2 mb-3">
                    <History className="w-5 h-5 text-blue-600" />
                    <h3 className="font-semibold text-gray-900">
                      Historial de este artículo
                    </h3>
                    {!loadingHistory && articleHistory.length > 0 && (
                      <Badge variant="secondary" className="ml-auto">
                        {articleHistory.length}
                      </Badge>
                    )}
                  </div>
                  {loadingHistory ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="h-6 w-6 animate-spin text-blue-600 mr-2" />
                      <span className="text-gray-600">Cargando historial...</span>
                    </div>
                  ) : articleHistory.length > 0 ? (
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      {articleHistory.map((item) => (
                      <div
                        key={item.IdCBParte}
                        className="flex items-start justify-between p-3 bg-gray-50 rounded-lg border border-gray-200"
                      >
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1 flex-wrap">
                            <Badge
                              variant="outline"
                              className="text-xs font-mono"
                            >
                              {item.CodigoBarrasParcial}
                            </Badge>
                            <Badge
                              className={
                                item.TipoProceso === "Moldura"
                                  ? "bg-green-600 text-xs"
                                  : "bg-purple-600 text-xs"
                              }
                            >
                              {item.TipoProceso}
                            </Badge>
                            {item.TipoRegistro === "Parcial" && (
                              <Badge
                                variant="secondary"
                                className="bg-orange-100 text-orange-700 text-xs"
                              >
                                Parcial
                              </Badge>
                            )}
                          </div>
                          <div className="flex items-center gap-3 text-xs text-gray-600">
                            <span className="flex items-center gap-1">
                              <Package className="h-3 w-3" />
                              {item.UnidPendProceso} uds
                            </span>
                            {item.FechaProceso && (
                              <span>
                                {(() => {
                                  try {
                                    const fecha = new Date(item.FechaProceso);
                                    
                                    // Si tenemos hora decimal (0.0 a 1.0), convertirla a horas, minutos y segundos
                                    if (item.HoraProceso !== undefined && item.HoraProceso !== null && item.HoraProceso > 0) {
                                      // HoraProceso es una fracción del día (0.0 = medianoche, 0.5 = mediodía, 1.0 = medianoche siguiente)
                                      const horasTotales = item.HoraProceso * 24;
                                      const horas = Math.floor(horasTotales);
                                      const minutosTotales = (horasTotales - horas) * 60;
                                      const minutos = Math.floor(minutosTotales);
                                      const segundos = Math.floor((minutosTotales - minutos) * 60);
                                      
                                      // Establecer la hora en la fecha
                                      fecha.setHours(horas, minutos, segundos, 0);
                                    }
                                    
                                    return fecha.toLocaleString("es-ES", {
                                      day: "2-digit",
                                      month: "2-digit",
                                      year: "numeric",
                                      hour: "2-digit",
                                      minute: "2-digit",
                                    });
                                  } catch {
                                    return item.FechaProceso;
                                  }
                                })()}
                              </span>
                            )}
                          </div>
                        </div>
                        <Button
                          onClick={() => handleUndoFromScanner(item)}
                          disabled={undoingHistory === item.IdCBParte}
                          variant="destructive"
                          size="sm"
                          className="ml-2 h-8 text-xs flex-shrink-0"
                        >
                          {undoingHistory === item.IdCBParte ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                          ) : (
                            "Deshacer"
                          )}
                        </Button>
                      </div>
                    ))}
                    </div>
                  ) : (
                    <div className="text-center py-4 text-gray-500 text-sm">
                      No hay historial disponible para este artículo
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default Scanner;